import React, { useCallback, useEffect, useRef, useState } from "react";
import styled from "@emotion/styled";
import ReactECharts from "echarts-for-react";
import * as echarts from "echarts/core";
import { GaugeChart, GaugeSeriesOption } from "echarts/charts";
import { CanvasRenderer } from "echarts/renderers";
import WidgetBox, { CustomSpinner } from "../WidgetBox";
import {
    fetchDetailDevices,
    getSensorLastReading,
} from "service/gatewayService";
import { WidgetProps, DeviceInfo } from "types";
import _, { isEmpty } from "lodash";
import {
    IconCircle,
    SensorContent,
    SensorInfo,
    SensorLabel,
    SensorType,
} from "../cards";
import SensorIcon from "components/gateways/SensorIcon";
import {
    formatTickLabel,
    formatValueWithUnit,
    isHttpSuccess,
    parseColorToHex,
    toFiniteReadingNumber,
} from "utils/functions";
import WidgetContent from "components/dashboard/widgets/WidgetContent";
import { dateTimeFormatter } from "utils/dateTimeFormatter";

echarts.use([GaugeChart, CanvasRenderer]);

const CardContainer = styled.div`
    padding-top: 1rem;
    min-height: 210px;
    position: relative;
    height: 100%;
    display: flex;
    flex-direction: column;
`;

const CardContent = styled.div``;

interface SensorGuageProps {
    device: DeviceInfo;
    refreshDateTime: string;
}

interface SensorGuageGradeProps extends SensorGuageProps {
    colorOffsets: [number, string][];
}

type EChartsOption = echarts.ComposeOption<GaugeSeriesOption>;

interface GaugeChartProps {
    value: number | null;
    min?: number;
    max?: number;
    color: string;
    customStyle?: (option: GaugeSeriesOption) => GaugeSeriesOption;
    unit: string;
    accuracy?: number;
    refreshDateTime?: string;
    chartKey?: string;
}

type GaugeProps = Pick<
    WidgetProps,
    "panel" | "isVisible" | "visibleRef" | "zoomLevel"
>;

/**
 * Calculate splitNumber for ECharts gauge axis based on min/max rules.
 * Handles negative min values.
 * @param min - Minimum value of the gauge (can be negative).
 * @param max - Maximum value of the gauge.
 * @returns Split number for gauge axis.
 *
 * @example
 * calculateSplitNumber(-2, 4)     // 6   (range=6, case1)
 * calculateSplitNumber(0, 40)     // 12  (case2)
 * calculateSplitNumber(-200, 200) // 10  (case3)
 * calculateSplitNumber(0, 800)    // 8   (case4)
 * calculateSplitNumber(0, 5000)   // 10  (fallback)
 */
export function calculateSplitNumber(min: number, max: number): number {
    const range = Math.abs(max - min);

    if (range <= 10) return range; // use max as ticks
    if (range <= 50) return 12;
    if (range <= 500) return 10;
    if (range <= 1000) return 8;

    return 6; // fallback
}
const GaugeChartComponent: React.FC<GaugeChartProps> = ({
    value,
    min = 0,
    max = 12,
    color,
    unit,
    customStyle,
    accuracy = 0,
    refreshDateTime = "-",
    chartKey = "gauge",
}) => {
    const isValidValue = typeof value === "number" && !Number.isNaN(value);

    const gaugeOption: GaugeSeriesOption = {
        type: "gauge",
        center: ["50%", "62%"],
        startAngle: 200,
        endAngle: -20,
        min,
        max,
        splitNumber: calculateSplitNumber(min, max),
        radius: "80%",
        axisLine: {
            lineStyle: {
                width: 7,
                color: [[1, "#444D5F"]],
            },
        },
        progress: { show: isValidValue, width: 7 },
        pointer: { show: false },
        axisTick: {
            distance: -15,
            length: 2,
            lineStyle: { width: 1, color: "#909192" },
        },
        splitLine: {
            distance: -18,
            length: 4,
            lineStyle: { width: 1.2, color: "#909192" },
        },
        axisLabel: {
            distance: -5,
            color: "#909192",
            fontSize: 10,
            rotate: "tangential",
            formatter: function (axisValue) {
                return formatTickLabel(axisValue, accuracy);
            },
        },
        detail: {
            valueAnimation: true,
            formatter: function (detailValue) {
                const isValid =
                    typeof detailValue === "number" &&
                    !Number.isNaN(detailValue);

                const valueText = isValid
                    ? formatValueWithUnit(
                        formatTickLabel(detailValue, accuracy),
                        unit
                    )
                    : "-";
                const refreshDateTimeText = isValid ? refreshDateTime : "-";
                return `{value|${valueText}}\n{time|${refreshDateTimeText}}`;
            },
            fontSize: 28,
            offsetCenter: [0, "70%"],
            color: color,
            rich: {
                value: {
                    fontSize: 28,
                    lineHeight: 30,
                    color: color,
                    fontWeight: 600,
                },
                time: {
                    fontSize: 12,
                    lineHeight: 18,
                    color: "#828fab",
                    fontWeight: 400,
                },
            },
        },
        data: [{ value: isValidValue ? Number(value) : "-", itemStyle: { color } }],
    };

    const option: EChartsOption = {
        series: [customStyle ? customStyle(gaugeOption) : gaugeOption],
    };

    return (
        <div style={{ width: "100%", height: "100%" }}>
            <ReactECharts
                key={chartKey}
                option={option}
                style={{ height: "100%", width: "100%" }}
            />
        </div>
    );
};

/**
 * Renders a Gauge chart component.
 * Fetches device information asynchronously and displays either a multi or single card
 * depending on the source type.
 *
 * @param {SensorGuageProps} props - The properties for the card component.
 * @returns {JSX.Element} The rendered guage chart component.
 */
const GaugeAny = ({ device, refreshDateTime }: SensorGuageProps) => {
    const { accuracy, unit, readings, chartColor } = device;
    const _accuracy = accuracy ?? 0;
    const color = parseColorToHex(chartColor);
    const readingValue = toFiniteReadingNumber(readings?.value);
    const lastActive = toFiniteReadingNumber(readings?.lastActive);

    // if lastActive is 0, then there is no current reading
    const hasCurrentReading = readingValue !== null && Boolean(lastActive);
    const gaugeValue = hasCurrentReading ? readingValue : null;

    return (
        <GaugeChartComponent
            min={device.min ?? 0}
            max={device.max ?? 100}
            value={gaugeValue}
            color={color}
            unit={unit}
            accuracy={_accuracy}
            refreshDateTime={refreshDateTime}
            chartKey={`gauge-any-${lastActive ?? 0}`}
        />
    );
};

/**
 * Renders a Gauge Grades chart component.
 * Fetches device information asynchronously and displays either a multi or single card
 * depending on the source type.
 *
 * @param {SensorGuageProps} props - The properties for the card component.
 * @returns {JSX.Element} The rendered guage chart component.
 */
const GaugeGrade = ({
    device,
    colorOffsets,
    refreshDateTime,
}: SensorGuageGradeProps) => {
    const { accuracy, unit, readings } = device;
    const _accuracy = accuracy ?? 0;
    const readingValue = toFiniteReadingNumber(readings?.value);
    const lastActive = toFiniteReadingNumber(readings?.lastActive);

    // if lastActive is 0, then there is no current reading
    const hasCurrentReading = readingValue !== null && Boolean(lastActive);
    const gaugeValue = hasCurrentReading ? readingValue : null;
    const isValidValue =
        typeof gaugeValue === "number" && !Number.isNaN(gaugeValue);

    const customStyle = (option: GaugeSeriesOption) => {
        return {
            ...option,
            startAngle: 180,
            endAngle: 0,
            radius: "80%",
            center: ["50%", "62%"],
            axisTick: {
                length: 2,
                distance: 5,
                lineStyle: {
                    color: "auto",
                    width: 1,
                },
            },
            splitLine: {
                distance: 5,
                length: 4,
                lineStyle: {
                    color: "auto",
                    width: 1.5,
                },
            },
            axisLine: {
                lineStyle: {
                    width: 7,
                    color: colorOffsets?.length
                        ? colorOffsets
                        : ([[1, "#828fab"]] as [number, string][]),
                },
            },
            progress: { show: false },
            pointer: {
                icon: "path://M12.8,0.7l12,40.1H0.7L12.8,0.7z",
                length: 10,
                width: 5,
                offsetCenter: [0, "-65%"],
                itemStyle: {
                    color: "auto",
                },
                show: isValidValue,
            },
            axisLabel: {
                show: false,
            },
            title: {
                offsetCenter: [0, "-10%"],
                fontSize: 13,
                color: "#828fab",
            },
            detail: {
                offsetCenter: [0, "30%"],
                valueAnimation: true,
                formatter: function (detailValue: number) {
                    const isValid =
                        typeof detailValue === "number" &&
                        !Number.isNaN(detailValue);

                    const valueText = isValid
                        ? formatValueWithUnit(
                            formatTickLabel(detailValue, _accuracy),
                            unit
                        )
                        : "-";
                    const refreshDateTimeText = isValid ? refreshDateTime : "-";
                    return `{value|${valueText}}\n{time|${refreshDateTimeText}}`;
                },
                fontSize: 28,
                color: "inherit",
                rich: {
                    value: {
                        fontSize: 28,
                        lineHeight: 30,
                        color: "inherit",
                        fontWeight: 600,
                    },
                    time: {
                        fontSize: 12,
                        lineHeight: 18,
                        color: "#828fab",
                        fontWeight: 400,
                    },
                },
            },
            data: [
                {
                    value: gaugeValue ?? ("-" as number | "-"),
                },
            ],
        };
    };

    return (
        <GaugeChartComponent
            min={device.min ?? 0}
            max={device.max ?? 100}
            value={gaugeValue}
            color={""}
            unit={unit}
            customStyle={customStyle}
            accuracy={_accuracy}
            refreshDateTime={refreshDateTime}
            chartKey={`gauge-grade-${lastActive ?? 0}`}
        />
    );
};

/**
 * Fetches and displays device information in a gauge format based on panel attributes.
 * It handles loading state and renders different gauge components based on the chart type.
 *
 * @param {GaugeProps} props - The properties passed to the component, including panel details and visibility state.
 * @returns {JSX.Element} - The rendered gauge component.
 * @throws {Error} - If fetching device information fails, an empty sensorDetails state is set.
 */
const Gauge = (props: GaugeProps) => {
    const {
        panel,
        panel: { attributes, name, chart },
        isVisible,
        visibleRef,
        zoomLevel,
    } = props;

    const [sensorDetails, setSensorDetails] = useState<DeviceInfo[]>([]);
    const [isLoaded, setIsLoaded] = useState(false);
    const isMountedRef = useRef(true);
    const refreshRate = 30 * 1000;
    const { sensorName, sensorType } = sensorDetails[0] ?? {};
    const device = sensorDetails[0] ?? {};
    const refreshDateTime = (() => {
        const ts = toFiniteReadingNumber(device?.readings?.lastActive);
        return ts ? dateTimeFormatter.dateTime(ts * 1000, true) : "-";
    })();

    const fetchDeviceInfo = async () => {
        const params = attributes.map(
            ({ gateway_id, device_id, said }: any) => ({
                gateway_id,
                device_id,
                said: said.toString(),
            })
        );
        try {
            const devices = await fetchDetailDevices(params);
            devices.forEach((d, index) => {
                d.chartColor = attributes[index].color;
                d.unit =
                    attributes[index].unit === "na"
                        ? d.unit
                        : attributes[index].unit;
                // Clear seeded readings so the dial only shows values from reading2.
                d.readings = {
                    ...d.readings,
                };
            });
            if (!isMountedRef.current) return;
            setSensorDetails(devices);
        } catch {
            if (!isMountedRef.current) return;
            setSensorDetails([]);
        } finally {
            if (isMountedRef.current) {
                setIsLoaded(true);
            }
        }
    };

    useEffect(() => {
        isMountedRef.current = true;
        return () => {
            isMountedRef.current = false;
        };
    }, []);

    const refreshReading = useCallback(async () => {
        const firstAttribute = attributes?.[0];
        if (!firstAttribute) return;

        const { gateway_id, device_id } = firstAttribute;
        const { data, status } = await getSensorLastReading(
            gateway_id,
            device_id
        );
        if (!isMountedRef.current) return;

        if (!isHttpSuccess(status) || !data) {
            setSensorDetails((devices) =>
                devices.map((d) => ({
                    ...d,
                    readings: {
                        ...d.readings,
                        value: 0,
                        lastActive: 0,
                    },
                }))
            );
            return;
        }

        setSensorDetails((devices) =>
            devices.map((d) => {
                const said = Number(d.said);
                const dataAny: any = data;
                const nextValue = toFiniteReadingNumber(dataAny?.value?.[said]);
                const nextTs = toFiniteReadingNumber(dataAny?.ts);
                return {
                    ...d,
                    readings: {
                        ...d.readings,
                        value: nextValue ?? 0,
                        lastActive: nextTs ?? 0,
                    },
                };
            })
        );
    }, [attributes, isMountedRef]);

    useEffect(() => {
        if (!isVisible) return;
        setIsLoaded(false);
        fetchDeviceInfo();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [panel, isVisible]);

    useEffect(() => {
        if (!isVisible) return;

        refreshReading();
        const readingInterval = setInterval(refreshReading, refreshRate);

        return () => {
            clearInterval(readingInterval);
        };
    }, [panel, isVisible, refreshRate, refreshReading]);

    const renderChart = () => {
        if (chart === "GAUGE") {
            return (
                <GaugeAny device={device} refreshDateTime={refreshDateTime} />
            );
        } else {
            const colors = Array.isArray(_.get(attributes, "[0].color"))
                ? attributes[0].color
                : [];
            const colorStops = colors.map((c: [number, string]) => [
                c[0],
                parseColorToHex(c[1]),
            ]);

            return (
                <GaugeGrade
                    device={device}
                    colorOffsets={colorStops}
                    refreshDateTime={refreshDateTime}
                />
            );
        }
    };

    return (
        <WidgetBox
            ref={visibleRef}
            className={
                zoomLevel <= 0.5
                    ? "widget-box w-100 h-30vh"
                    : "widget-box w-100"
            }
        >
            <WidgetContent title={name}>
                <CardContainer>
                    <SensorContent className="">
                        <IconCircle size={35} className="mr-2">
                            <SensorIcon sensorType={sensorType} cls={device.cls} />
                        </IconCircle>
                        <SensorInfo>
                            <SensorLabel
                                fontSize={16}
                                className="text-truncate"
                            >
                                {sensorName}
                            </SensorLabel>
                            <SensorType fontSize={14} className="text-truncate">
                                {sensorType}
                            </SensorType>
                        </SensorInfo>
                    </SensorContent>
                    {isLoaded && (
                        <CardContent className={"mt-md-auto flex-fill"}>
                            {!isEmpty(device) && renderChart()}
                        </CardContent>
                    )}

                    {!isLoaded && (
                        <div className="d-flex justify-content-center align-items-center mt-n5 flex-fill">
                            <CustomSpinner animation="border" />
                        </div>
                    )}
                </CardContainer>
            </WidgetContent>
        </WidgetBox>
    );
};

export default Gauge;
